import React, { useState, useEffect, useRef, useCallback } from "react";
import Layout from "../../../core/layout";
import { BrowserMultiFormatReader } from "@zxing/library";
import { Button, ElementContainer, ElementParentContainer } from "../../../core/elements";
import { getData } from "../../../../backend/api";

const Scanner = (props) => {
  useEffect(() => {
    document.title = `Scanner - Barcode Generator Portal`;
  }, []);

  const [productData, setProductData] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [scanRequest, setScanRequest] = useState(0);
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  // Define stopWebcam first since it's used in startScanning
  const stopWebcam = useCallback(() => {
    const tracks = videoRef.current?.srcObject?.getTracks() || [];
    tracks.forEach((track) => track.stop());
  }, []);

  // Now we can include stopWebcam in startScanning's dependencies
  const startScanning = useCallback(async () => {
    try {
      if (videoRef.current && scannerRef.current) {
        const result = await scannerRef.current.decodeOnceFromConstraints({ video: { facingMode: { ideal: "environment" } } }, videoRef.current);
        if (result) {
          const barcode = result.text;
          if (barcode) {
            const response = await getData({ barcode: encodeURIComponent(barcode) }, "product");
            if (response && response.data && response.data.response) {
              setProductData(response.data.response);
              stopWebcam(); // Stop webcam stream after getting product details
              setErrorMessage(""); // Clear error message on successful scan
            } else {
              setErrorMessage("Barcode not found. Please scan again.");
            }
          } else {
            setErrorMessage("Invalid barcode. Please scan again.");
          }
        } else {
          setErrorMessage("Invalid barcode. Please scan again.");
        }
      } else {
        setErrorMessage("Webcam video not available. Please try again.");
      }
    } catch (error) {
      console.error("Error scanning:", error.message || error);
      setErrorMessage("An error occurred while scanning. Please try again.");
    }
  }, [stopWebcam]); // Include stopWebcam in dependencies

  const handleScanAgain = useCallback(() => {
    setProductData({}); // Clear product data
    setErrorMessage(""); // Clear error message
    setScanRequest((request) => request + 1);
  }, []);

  // Initialize scanner and start scanning
  useEffect(() => {
    let isMounted = true;

    const initializeScanner = async () => {
      if (isMounted) {
        scannerRef.current = new BrowserMultiFormatReader();
        await startScanning();
      }
    };

    initializeScanner();

    // Cleanup function
    return () => {
      isMounted = false;
      if (scannerRef.current) {
        scannerRef.current.reset();
      }
      stopWebcam();
    };
  }, [scanRequest, startScanning, stopWebcam]);

  const detailRows = [
    ["Product Name", productData.productName],
    ["Part Number", productData.partNumberCode],
    ["Internal Barcode", productData.barcode],
    ["GS1 Barcode", productData.gs1Barcode],
    ["GS1 Carton Barcode", productData.gs1CartoonBarcode],
    ["Category", productData.category?.title],
    ["Packaging", productData.packaging?.title],
    ["Grade", productData.grade?.title],
    ["API 1", productData.api1?.title],
    ["API 2", productData.api2?.title],
    ["ACEA", productData.acea?.title],
    ["SPEC", productData.countryOilGrade?.title],
    ["Country", productData.countryCode?.title],
    ["Country Version", productData.countryVersion?.title],
    ["Product name in Carton", productData.description],
  ].filter(([, value]) => value !== undefined && value !== null && value !== "" && value !== "NIL");

  return (
    <>
      <ElementParentContainer className="column">
        <ElementContainer>
          {errorMessage ? (
            <div>
              {errorMessage}
              <Button ClickEvent={handleScanAgain} icon={"next"} value="Scan Again" />
            </div>
          ) : Object.keys(productData).length === 0 ? (
            <div>
              <video id="video" ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "auto", maxWidth: "100%" }} />
            </div>
          ) : (
            <div>
              <h2>Product Details</h2>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
              >
                <tbody>
                  {detailRows.map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                        <strong>{label}</strong>
                      </td>
                      <td style={{ padding: "10px", border: "1px solid #ddd" }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {productData.link && (
                <p>
                  <a href={productData.link} target="_blank" rel="noreferrer">
                    Open Product Link
                  </a>
                </p>
              )}
              <Button ClickEvent={handleScanAgain} icon={"next"} value="Scan Again" />
            </div>
          )}
        </ElementContainer>
      </ElementParentContainer>
    </>
  );
};

export default Layout(Scanner);
